<template>
  <section class="prices-block" ref="root">
    <div class="prices-block__inner" :class="{ 'prices-block__inner_visible': visible }">
      <h1 class="prices-block__title">Стоимость картин</h1>
      <p class="prices-block__subtitle">
        Цена зависит от размера и аксессуаров —<br />
        чем больше формат, тем выразительнее работа в интерьере.
      </p>
      <div class="prices-block__accessories-wrap">
        <a href="#accessories" class="prices-block__accessories-btn" @click.prevent="scrollToAccessories">
          <span class="prices-block__accessories-btn-text">Посмотреть аксессуары</span>
          <span class="prices-block__accessories-btn-icon" aria-hidden="true">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M12 5v14M19 12l-7 7-7-7"/></svg>
          </span>
        </a>
      </div>
      <div v-if="loading && !rows.length" class="prices-block__loading">
        <Loader inverse size="lg" />
      </div>
      <div v-else class="prices-block__wrap">
        <div class="prices-block__table-title">Доступные размеры и цены</div>
        <div
          class="prices-block__row"
          :class="{
            'prices-block__row_accessories': row.withAccessories,
            'prices-block__row_info': isInfoRow(row)
          }"
          v-for="(row, i) in rows"
          :key="row.size ? row.size : `info-${i}`"
          :style="{ transitionDelay: `${0.12 + i * 0.06}s` }"
        >
          <div class="prices-block__cell-size" :class="{ 'prices-block__cell-size_info': isInfoRow(row) }">
            <span v-if="isInfoRow(row)" class="prices-block__info-icon" aria-hidden="true">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4M12 8h.01"/></svg>
            </span>
            <span v-else class="prices-block__size">{{ row.size }}</span>
            <template v-if="isInfoRow(row) || row.withAccessories">
              <div v-if="descriptionParts(row.shortDescription).length" class="prices-block__note prices-block__note_parts" :class="{ 'prices-block__note_lead': isInfoRow(row) }">
                <span v-for="(part, k) in descriptionParts(row.shortDescription)" :key="k" class="prices-block__part">{{ part }}</span>
              </div>
              <span v-else class="prices-block__note" :class="{ 'prices-block__note_lead': isInfoRow(row) }">{{ row.shortDescription }}</span>
            </template>
          </div>
          <template v-if="!isInfoRow(row)">
            <span class="prices-block__price">{{ row.price }}</span>
            <button type="button" class="prices-block__btn" @click="openOrderModal(row)">Выбрать</button>
          </template>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { ref, onMounted, computed } from 'vue'
import { useStore } from 'vuex'
import { formatPrice } from '@/data/prices'
import Loader from '@/components/Loader.vue'

export default {
  name: 'PricesTable',
  components: { Loader },
  setup() {
    const store = useStore()
    const root = ref(null)
    const visible = ref(false)

    function openOrderModal(row) {
      store.commit('setOrderModalPreselectedSizeKey', row.sizeKey)
      store.commit('setOrderModalOpen', true)
    }

    function scrollToAccessories() {
      window.scrollBy({ top: 800, behavior: 'smooth' })
    }

    function isInfoRow(row) {
      return !row.size || String(row.size).trim() === ''
    }

    function descriptionParts(desc) {
      if (!desc || typeof desc !== 'string') return []
      const parts = desc.split('|').map((s) => s.trim()).filter(Boolean)
      return parts.length > 1 ? parts : []
    }

    onMounted(() => {
      store.dispatch('price/fetchPrices')
      requestAnimationFrame(() => { visible.value = true })
    })

    const rows = computed(() => {
      const items = store.getters['price/priceRows'] || []
      return items.map((r) => ({
        sizeKey: r.sizeKey,
        size: r.sizeLabel,
        price: formatPrice(r.price),
        withAccessories: r.withAccessories,
        shortDescription: r.shortDescription,
      }))
    })
    const loading = computed(() => store.getters['price/loading'])

    return { root, visible, rows, loading, openOrderModal, scrollToAccessories, isInfoRow, descriptionParts }
  }
}
</script>

<style lang="scss" scoped>
@import "../../styles/_variables.scss";

$dur: 0.7s;
$ease: cubic-bezier(0.22, 1, 0.36, 1);

.prices-block {
  position: relative;
  min-height: 60vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: $color-bg-alt;
  color: $color-primary-inverse;
  padding: 80px $gap 80px;

  @media (max-width: $breakpoint-mobile) {
    min-height: auto;
    padding: 60px $gap 56px;
  }

  &__inner {
    max-width: $container-max;
    margin: 0 auto;
    width: 100%;
  }

  &__title {
    font-size: clamp(32px, 5vw, 48px);
    font-weight: 800;
    letter-spacing: -0.02em;
    margin: 0 0 20px;
    text-align: center;
    opacity: 0;
    transform: translateY(24px);
    transition: opacity $dur $ease, transform $dur $ease;

    .prices-block__inner_visible & {
      opacity: 1;
      transform: translateY(0);
    }
  }

  &__subtitle {
    font-size: clamp(16px, 2vw, 19px);
    line-height: 1.55;
    margin: 0 0 40px;
    max-width: 520px;
    margin-left: auto;
    margin-right: auto;
    text-align: center;
    opacity: 0;
    transform: translateY(20px);
    transition: opacity $dur $ease 0.08s, transform $dur $ease 0.08s;

    .prices-block__inner_visible & {
      opacity: 0.85;
      transform: translateY(0);
    }
  }

  &__accessories-wrap {
    text-align: center;
    margin-bottom: 20px;
  }

  &__accessories-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    margin: 0;
    padding: 14px 32px;
    font-size: 16px;
    font-weight: 700;
    letter-spacing: 0.04em;
    color: $color-primary-inverse;
    background: $color-accent-teal;
    border: none;
    border-radius: 12px;
    text-decoration: none;
    font-family: inherit;
    cursor: pointer;
    box-shadow: 0 8px 24px rgba(97, 130, 111, 0.45);
    opacity: 0;
    transform: translateY(12px);
    transition: opacity $dur $ease, transform $dur $ease, opacity 0.2s, transform 0.15s, box-shadow 0.2s;

    .prices-block__inner_visible & {
      opacity: 1;
      transform: translateY(0);
    }

    &:hover {
      opacity: 0.95;
      box-shadow: 0 12px 32px rgba(97, 130, 111, 0.55);
    }

    &:active {
      transform: scale(0.98);
    }

    &:focus-visible {
      outline: 2px solid $color-accent-teal;
      outline-offset: 4px;
    }
  }

  &__accessories-btn-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;

    svg {
      display: block;
      width: 100%;
      height: 100%;
    }
  }

  &__loading {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 200px;
    padding: 40px;
  }

  &__wrap {
    background: $color-bg;
    border: 1px solid rgba(255, 255, 255, 0.15);
    border-radius: 16px;
    overflow: hidden;
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.3);
  }

  &__table-title {
    padding: 20px 24px;
    font-size: clamp(14px, 1.4vw, 16px);
    font-weight: 700;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    text-align: left;
    color: $color-primary;
    background: rgba(97, 130, 111, 0.25);
    border-bottom: 1px solid rgba(255, 255, 255, 0.12);
  }

  &__row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 20px;
    padding: 20px 28px;
    border-bottom: 1px solid $color-border;
    opacity: 0;
    transform: translateX(-20px);
    transition: opacity $dur $ease, transform $dur $ease, background 0.2s;

    &:last-child {
      border-bottom: none;
    }

    &:hover {
      background: rgba(0, 0, 0, 0.03);
    }

    .prices-block__inner_visible & {
      opacity: 1;
      transform: translateX(0);
    }

    @media (max-width: $breakpoint-mobile) {
      flex-wrap: wrap;
      padding: 18px 20px;
      gap: 12px;
    }

    &_info {
      background: rgba(97, 130, 111, 0.12);
      border-left: 4px solid $color-accent-teal;
      gap: 14px;

      &:hover {
        background: rgba(97, 130, 111, 0.18);
      }
    }
  }

  &__info-icon {
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    color: $color-accent-teal;

    svg {
      display: block;
      width: 100%;
      height: 100%;
    }
  }

  &__cell-size {
    display: flex;
    flex-direction: column;
    gap: 4px;

    &_info {
      flex-direction: row;
      align-items: center;
      gap: 12px;
    }
  }

  &__size {
    font-size: clamp(17px, 1.9vw, 21px);
    font-weight: 700;
    color: $color-text;
    letter-spacing: -0.02em;
  }

  &__note {
    font-size: 12px;
    color: $color-text-muted;
    font-weight: 500;

    &_lead {
      font-size: 14px;
      line-height: 1.5;
      color: $color-text;
      font-weight: 500;
    }

    &_parts {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 6px 0;
      line-height: 1.4;
      font-size: 13px;
      color: $color-text;
    }
  }

  &__part {
    display: inline-flex;
    align-items: center;
    font-size: inherit;
    color: inherit;
    font-weight: inherit;

    &::after {
      content: '·';
      margin-left: 6px;
      margin-right: 4px;
      opacity: 0.6;
      font-weight: 400;
    }

    &:last-child::after {
      display: none;
    }
  }

  &__price {
    font-size: clamp(19px, 2.1vw, 24px);
    font-weight: 800;
    color: $color-primary;
    letter-spacing: -0.02em;
    margin-left: auto;

    @media (max-width: $breakpoint-mobile) {
      margin-left: 0;
      order: 3;
      width: 100%;
    }
  }

  &__btn {
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 10px 22px;
    background: $color-accent-teal;
    color: $color-primary-inverse;
    font-size: 14px;
    font-weight: 700;
    font-family: inherit;
    text-decoration: none;
    border: none;
    border-radius: 50px;
    cursor: pointer;
    transition: transform 0.2s, box-shadow 0.2s;

    &:hover {
      background: darken($color-accent-teal, 6%);
      transform: scale(1.05);
      box-shadow: 0 4px 16px rgba(13, 148, 136, 0.35);
    }
  }
}
</style>
